import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * Google reCAPTCHA v2 checkbox widget.
 *
 * Site key is picked up from REACT_APP_RECAPTCHA_SITE_KEY at build
 * time. When the key is blank the component renders nothing and calls
 * onChange(null) — dev builds without a key still work, and the auth
 * screens don't gate submit on a widget that isn't there.
 *
 * Behavior:
 *   - Loads https://www.google.com/recaptcha/api.js once per page
 *     (via a shared promise so multiple mounts share the fetch).
 *   - On mount, renders into a fresh div (grecaptcha reuses the same
 *     one otherwise and blows up on remount / language switch).
 *   - Calls onChange(token) on solve, onChange(null) on expire or
 *     error, so the parent's disabled-until-solved gate stays honest.
 *   - Exposes an imperative `reset()` handle via ref. Callers invoke
 *     it from their error handlers because v2 tokens are single-use:
 *     a failed login/register leaves a dead token in state, and the
 *     next submit would then always fail with "Captcha check failed".
 */
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const SCRIPT_ID = 'g-recaptcha-script';

let scriptPromise = null;

function loadRecaptchaScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('reCAPTCHA needs a browser'));
      return;
    }
    if (window.grecaptcha && window.grecaptcha.render) {
      resolve();
      return;
    }
    // Explicit-render mode via ?render=explicit + onload callback keeps
    // us in control of when widgets appear (mount time, not global
    // window-load), which matters on client-routed pages.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // Race: another Recaptcha mounted first. Poll for grecaptcha to
      // materialize — the shared promise means only one poller runs.
      const start = Date.now();
      const tick = () => {
        if (window.grecaptcha && window.grecaptcha.render) return resolve();
        if (Date.now() - start > 10000) return reject(new Error('reCAPTCHA load timed out'));
        setTimeout(tick, 100);
      };
      tick();
      return;
    }
    window.__onRecaptchaLoaded = () => resolve();
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoaded&render=explicit';
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    document.head.appendChild(s);
  });

  return scriptPromise;
}

const Recaptcha = forwardRef(function Recaptcha({ onChange }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset() {
      // No-op when the widget hasn't rendered (no key, loader failed,
      // etc.) — callers invoke this unconditionally in error handlers.
      if (widgetIdRef.current === null || !window.grecaptcha?.reset) return;
      try {
        window.grecaptcha.reset(widgetIdRef.current);
        // Reset clears the token on Google's side; keep the parent in
        // sync so its submit gate re-disables until the user re-ticks.
        onChange?.(null);
      } catch {
        /* widget already gone — ignore */
      }
    },
  }), [onChange]);

  useEffect(() => {
    if (!SITE_KEY) {
      // No site key at build time — treat captcha as disabled so the
      // form still submits on dev builds.
      onChange?.(null);
      return undefined;
    }

    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        // In a client-routed app the same DOM node can survive across
        // mounts; make a fresh child each time so grecaptcha's "widget
        // already rendered" guard doesn't fire.
        const host = document.createElement('div');
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(host);
        widgetIdRef.current = window.grecaptcha.render(host, {
          sitekey: SITE_KEY,
          callback: (token) => onChange?.(token || null),
          'expired-callback': () => onChange?.(null),
          'error-callback': () => onChange?.(null),
        });
      })
      .catch(() => {
        // Loader gave up (offline, blocked). Surface null so the parent
        // can decide whether to allow submit — auth pages keep the
        // button disabled, so a blocked user just sees the widget slot
        // stay empty.
        if (!cancelled) onChange?.(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch {
          /* widget already gone — nothing to reset */
        }
      }
    };
    // onChange isn't in deps on purpose — the parent's memoised
    // setter shouldn't re-render the widget, which would fight
    // grecaptcha's internal state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;

  return (
    <div
      ref={containerRef}
      className="my-2 flex justify-center"
      data-testid="recaptcha-widget"
    />
  );
});

export default Recaptcha;

// Callers use this to know whether they should gate submit on a token.
export const RECAPTCHA_ENABLED = Boolean(SITE_KEY);
